import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { S3AccessKeySecret, S3_CAPABILITY_PRESETS } from '../../types/api';
import Logger from '../../utils/logger';
import { getErrorMessage } from '../../utils/errorHandler';

interface CreateAccessKeyModalProps {
  bucketId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (secret: S3AccessKeySecret) => void;
}

interface FormValues {
  name: string;
  preset: 'read-only' | 'read-write' | 'full';
}

const CreateAccessKeyModal: React.FC<CreateAccessKeyModalProps> = ({ bucketId, isOpen, onClose, onCreated }) => {
  const { t } = (useTranslation as any)('nubis3');
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<FormValues>({
    defaultValues: { preset: 'read-write' },
  });
  const [submitting, setSubmitting] = useState(false);
  const selectedPreset = watch('preset');

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const capabilities = S3_CAPABILITY_PRESETS[values.preset];
      const secret = await apiService.createS3AccessKey(bucketId, {
        name: values.name.trim(),
        capabilities,
      });
      reset();
      onCreated(secret);
      onClose();
    } catch (err: unknown) {
      Logger.error('Create S3 access key failed', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (err as any)?.response?.status;
      if (status === 409) {
        toast.error(t('errors.duplicateName'));
      } else if (status === 502 || status === 503) {
        toast.error(t('errors.b2Unavailable'));
      } else {
        toast.error(getErrorMessage(err, t('accessKeys.form.errorDefault') as string));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const presets: { value: FormValues['preset']; labelKey: string; descKey: string }[] = [
    { value: 'read-only', labelKey: 'accessKeys.form.presetReadOnly', descKey: 'accessKeys.form.presetReadOnlyDesc' },
    { value: 'read-write', labelKey: 'accessKeys.form.presetReadWrite', descKey: 'accessKeys.form.presetReadWriteDesc' },
    { value: 'full', labelKey: 'accessKeys.form.presetFull', descKey: 'accessKeys.form.presetFullDesc' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-1 rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-1">
          <h2 className="text-lg font-semibold text-text-primary">{t('accessKeys.form.title')}</h2>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-text-primary" aria-label="close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              {t('accessKeys.form.name')} *
            </label>
            <input
              type="text"
              placeholder={t('accessKeys.form.namePlaceholder') as string}
              {...register('name', {
                required: true,
                pattern: /^[a-zA-Z0-9_-]+$/,
                minLength: 1,
                maxLength: 60,
              })}
              className="w-full px-3 py-2 rounded-md border border-border-1 bg-surface-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
            <p className="mt-1 text-xs text-text-secondary">{t('accessKeys.form.nameHelp')}</p>
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{t('accessKeys.form.nameHelp')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('accessKeys.form.preset')} *
            </label>
            <div className="space-y-2">
              {presets.map(({ value, labelKey, descKey }) => (
                <label
                  key={value}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    selectedPreset === value
                      ? 'border-accent-500 bg-accent-500/5'
                      : 'border-border-1 hover:bg-surface-2'
                  }`}
                >
                  <input
                    type="radio"
                    value={value}
                    {...register('preset', { required: true })}
                    className="mt-1 accent-accent-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-text-primary">{t(labelKey)}</div>
                    <div className="text-xs text-text-secondary mt-0.5">{t(descKey)}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium text-text-secondary hover:bg-surface-2"
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-medium bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50"
            >
              {submitting ? t('accessKeys.form.submitting') : t('accessKeys.form.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAccessKeyModal;
